import { useState } from 'react';
import { formatDisplayDate } from '../../utils/format';
import { LoadingPage } from '../../components/common/Spinner';
import { Button } from '../../components/common/Button';
import { ConfirmModal } from '../../components/common/Modal';
import {
  ExternalUserStatusBadge,
  InviteExternalUserModal,
  EditExternalUserProjectsModal,
} from '../../components/external-users';
import {
  useExternalUsers,
  useResendInvite,
  useSoftDeleteExternalUser,
} from '../../hooks/useExternalUsers';
import type {
  ExternalUserRow,
  ExternalUserProjectBinding,
} from '../../services/api/externalUsers';

/**
 * Admin External Users page (ADMIN-02 UI / ROUTE-01).
 *
 * Composes the four primitives from `frontend/src/components/external-users/`
 * into a paginated table with Invite, Resend invite, Edit projects, and
 * Soft-delete affordances. Page route is gated by `<AdminRoute>` in App.tsx;
 * backend `requireAdmin` middleware (Phase 14) is the final authority.
 *
 * Page shape mirrors `AppAccessMatrixPage` (15-PATTERNS.md analog):
 *   header row → loading / error / empty gate → table → pagination → modals.
 */

const PER_PAGE = 25;

function formatProjects(projects: ExternalUserProjectBinding[]): string {
  if (projects.length === 0) return '—';
  if (projects.length <= 2) return projects.map((p) => p.name).join(', ');
  const head = projects.slice(0, 2).map((p) => p.name).join(', ');
  const more = projects.length - 2;
  return `${head} + ${more} more`;
}

function formatDate(iso: string): string {
  return formatDisplayDate(iso);
}

export default function ExternalUsersPage() {
  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch } = useExternalUsers(page, PER_PAGE);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ExternalUserRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ExternalUserRow | null>(null);

  const resendMutation = useResendInvite();
  const softDeleteMutation = useSoftDeleteExternalUser();

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.perPage)) : 1;

  return (
    <div>
      {/* Header row */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold text-gray-900">External Users</h1>
        <Button
          variant="primary"
          size="md"
          onClick={() => setInviteOpen(true)}
        >
          Invite external user
        </Button>
      </div>

      {/* Loading / error / empty / table gates */}
      {isLoading && !data && <LoadingPage />}

      {error && !data && (
        <div className="text-center py-12">
          <p className="text-gray-700">Couldn&apos;t load external users — try refreshing.</p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-3"
            onClick={() => refetch()}
          >
            Retry load
          </Button>
        </div>
      )}

      {data && data.rows.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No external users yet.</p>
          <p className="mt-1 text-sm text-gray-400">
            Click &quot;Invite external user&quot; to get started.
          </p>
        </div>
      )}

      {data && data.rows.length > 0 && (
        <>
          <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
            <table className="divide-y divide-gray-200 w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
                  >
                    Email
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
                  >
                    Projects
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
                  >
                    Invited by
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
                  >
                    Invited at
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
                  >
                    Last login
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.rows.map((row) => (
                  <tr
                    key={row.id}
                    className={row.status === 'deleted' ? 'opacity-60' : ''}
                  >
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">
                      {row.firstName} {row.lastName}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">{row.email}</td>
                    <td className="px-4 py-2">
                      <ExternalUserStatusBadge status={row.status} />
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {formatProjects(row.projects)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {row.invitedBy
                        ? `${row.invitedBy.firstName} ${row.invitedBy.lastName}`
                        : '—'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {formatDate(row.invitedAt)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">
                      {row.lastLoginAt ? formatDate(row.lastLoginAt) : 'Never'}
                    </td>
                    <td className="px-4 py-2 text-right text-sm">
                      {row.status === 'deleted' ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        <div className="flex justify-end gap-1">
                          {row.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => resendMutation.mutate(row.id)}
                              isLoading={
                                resendMutation.isPending &&
                                resendMutation.variables === row.id
                              }
                            >
                              Resend invite
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditTarget(row)}
                          >
                            Edit projects
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => setDeleteTarget(row)}
                          >
                            Delete user
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination — UI-SPEC §"Pagination controls": Previous / Next + "Page {n}" */}
          {totalPages > 1 && (
            <nav
              aria-label="Pagination"
              className="mt-4 flex items-center justify-between"
            >
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                aria-disabled={page <= 1}
              >
                &larr; Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                aria-disabled={page >= totalPages}
              >
                Next &rarr;
              </Button>
            </nav>
          )}
        </>
      )}

      {/* Modals — always mounted; visibility driven by isOpen/user props */}
      <InviteExternalUserModal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />
      <EditExternalUserProjectsModal
        user={editTarget}
        onClose={() => setEditTarget(null)}
      />
      {deleteTarget && (
        <ConfirmModal
          isOpen
          onClose={() => setDeleteTarget(null)}
          onConfirm={() =>
            softDeleteMutation.mutate(deleteTarget.id, {
              onSuccess: () => setDeleteTarget(null),
            })
          }
          title="Delete external user"
          message={`This will revoke ${deleteTarget.firstName} ${deleteTarget.lastName}'s access. Their data will be retained for audit purposes. This action cannot be undone.`}
          confirmText="Delete user"
          cancelText="Keep user"
          variant="danger"
          isLoading={softDeleteMutation.isPending}
        />
      )}
    </div>
  );
}
